/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

// TODO: assign to correct module
import { useEffect, useState } from "react";
import {
  getTicket,
  getTicketMessages,
  postReply,
  setTicketStatus,
  updateTicketFields,
  deleteTicket,
  TICKET_STATUSES,
  TICKET_CATEGORIES,
  getTicketAreaGroups,
} from "../utils/ticketService.js";
import { FilePicker, AttachmentList } from "./Attachments.jsx";

// One ticket conversation. Used by the user Help page (isAdmin=false) and the
// staff inbox (isAdmin=true). The opening message is the ticket's original body.
export default function TicketThread({ ticketId, isAdmin = false, submitterEmail, onBack, onChanged }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editModule, setEditModule] = useState("");

  async function load() {
    setErr(null);
    try {
      const [t, m] = await Promise.all([getTicket(ticketId), getTicketMessages(ticketId)]);
      setTicket(t);
      setMessages(m);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  async function send(e) {
    e.preventDefault();
    if (!reply.trim() && replyFiles.length === 0) return;
    setBusy(true);
    setErr(null);
    try {
      await postReply(ticketId, reply.trim(), replyFiles);
      setReply("");
      setReplyFiles([]);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function changeStatus(s) {
    setBusy(true);
    setErr(null);
    try {
      await setTicketStatus(ticketId, s);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  function startEdit() {
    setEditSubject(ticket.subject || "");
    setEditCategory(ticket.category || "");
    setEditModule(ticket.module || "");
    setEditing(true);
  }

  async function saveEdit() {
    setBusy(true);
    setErr(null);
    try {
      await updateTicketFields(ticketId, {
        subject: editSubject,
        category: editCategory || null,
        module: editModule || null,
      });
      setEditing(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function doDelete() {
    if (!window.confirm("Delete this ticket permanently? This can't be undone.")) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteTicket(ticketId);
      onChanged && onChanged();
      onBack && onBack();
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (!ticket) return <p className="error">{err || "Ticket not found."}</p>;

  // Opening message = the original ticket body (always from the submitter).
  const thread = [
    { id: "opening", author_role: "submitter", body: ticket.body, created_at: ticket.created_at, attachments: ticket.attachments },
    ...messages,
  ];

  function whoLabel(m) {
    if (m.author_role === "admin") return "Brand Command Support";
    if (isAdmin) return submitterEmail || m.author_email || "Customer";
    return "You";
  }

  return (
    <div>
      <button className="link" onClick={onBack} style={{ paddingLeft: 0 }}>← Back to tickets</button>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, margin: "10px 0 2px" }}>
        <div>
          <div className="muted" style={{ fontSize: 12 }}>BC-{ticket.ticket_number}</div>
          <h2 style={{ margin: "2px 0 0" }}>{ticket.subject}</h2>
        </div>
        <span className={`ticket-status ${ticket.status}`}>{ticket.status}</span>
      </div>
      <p className="muted" style={{ margin: "4px 0 6px" }}>
        {[ticket.category, ticket.module].filter(Boolean).join(" · ") || "—"}
      </p>
      {isAdmin && !editing && (
        <div style={{ display: "flex", gap: 16, margin: "0 0 6px" }}>
          <button type="button" className="link" style={{ padding: 0 }} onClick={startEdit}>Edit</button>
          <button type="button" className="link" style={{ padding: 0, color: "var(--error)" }} disabled={busy} onClick={doDelete}>Delete</button>
        </div>
      )}
      {isAdmin && editing && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, margin: "8px 0 14px", padding: 12, background: "var(--sunken, #eef0f4)", borderRadius: 10 }}>
          <input value={editSubject} onChange={(e) => setEditSubject(e.target.value)} maxLength={200} placeholder="Subject" style={{ margin: 0 }} />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={editCategory} onChange={(e) => setEditCategory(e.target.value)} style={{ width: "auto", margin: 0 }}>
              <option value="">— Category —</option>
              {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={editModule} onChange={(e) => setEditModule(e.target.value)} style={{ width: "auto", margin: 0 }}>
              <option value="">— Area —</option>
              {getTicketAreaGroups().map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((m) => <option key={m} value={m}>{m}</option>)}
                </optgroup>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn" type="button" disabled={busy || !editSubject.trim()} onClick={saveEdit}>Save</button>
            <button type="button" className="secondary" disabled={busy} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 12, margin: "16px 0" }}>
        {thread.map((m) => {
          const fromSupport = m.author_role === "admin";
          return (
            <div
              key={m.id}
              style={{
                alignSelf: fromSupport ? "flex-start" : "flex-end",
                maxWidth: "min(88%, 640px)",
                background: fromSupport ? "var(--sunken, #eef0f4)" : "var(--primary-soft, #e8edf5)",
                borderRadius: 12,
                padding: "10px 13px",
              }}
            >
              <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
                {whoLabel(m)} · {new Date(m.created_at).toLocaleString()}
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{m.body}</div>
              <AttachmentList attachments={m.attachments} />
            </div>
          );
        })}
      </div>

      <form onSubmit={send}>
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={3}
          maxLength={5000}
          placeholder={isAdmin ? "Reply to the customer…" : "Add a reply…"}
        />
        <div style={{ marginTop: 8 }}>
          <FilePicker files={replyFiles} setFiles={setReplyFiles} disabled={busy} />
        </div>
        <div style={{ display: "flex", flexDirection: "row", gap: 10, alignItems: "center", marginTop: 8 }}>
          <button className="btn" type="submit" disabled={busy || (!reply.trim() && replyFiles.length === 0)}>Send reply</button>
          {isAdmin && (
            <label style={{ flexDirection: "row", alignItems: "center", gap: 8, fontSize: 13, margin: 0 }}>
              Status
              <select value={ticket.status} disabled={busy} onChange={(e) => changeStatus(e.target.value)} style={{ width: "auto", margin: 0 }}>
                {TICKET_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
          )}
        </div>
      </form>
      {err && <p className="error" style={{ marginTop: 10 }}>{err}</p>}
    </div>
  );
}
